import {
  addDaysIso,
  MEAL_STATUS_LABELS,
  type MealDayLine,
  type MealMyDay,
  type MealStatus,
  mealEats,
  WEEKDAY_LABELS,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Copy, Download, Lock, Search, Trash2, UserPlus } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { EmptyPanel } from '@/components/crm-bits';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  Segmented,
  Select,
  Spinner,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { todayIso } from '@/lib/format';
import {
  atText,
  dayMonthText,
  dayTitle,
  mealDayQuery,
  mealDaysQuery,
  mealReportQuery,
  mealSubscriptionsQuery,
  mealsMetaQuery,
  mealsMyQuery,
  portions,
  weekdayShort,
} from '@/lib/meals';

const useManage = () => useCan()('meals.manage');

const errorText = (err: unknown) =>
  err instanceof ApiError
    ? (Object.values(err.fields)[0] ?? err.message)
    : err instanceof Error
      ? err.message
      : null;

function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
    >
      {children}
    </p>
  );
}

const STATUS_TONE: Record<MealStatus, 'ok' | 'warn' | 'neutral' | 'danger'> = {
  yes: 'ok',
  extra: 'ok',
  skip: 'neutral',
  absent: 'warn',
  no: 'neutral',
  off: 'neutral',
};

function StatusBadge({ status }: { status: MealStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{MEAL_STATUS_LABELS[status]}</Badge>;
}

export function MealsNav() {
  const manage = useManage();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabs = [
    { to: '/meals', label: 'Заказ', show: manage },
    { to: '/meals/my', label: 'Мой обед', show: true },
    { to: '/meals/people', label: 'Кто обедает', show: manage },
    { to: '/meals/report', label: 'Отчёт', show: manage },
    { to: '/meals/settings', label: 'Настройки', show: manage },
  ];
  return (
    <nav
      aria-label="Разделы обедов"
      className="-mx-4 mb-5 overflow-x-auto border-b border-line px-4 lg:-mx-8 lg:px-8"
    >
      <ul className="flex gap-1">
        {tabs
          .filter((t) => t.show)
          .map((t) => {
            const active = pathname === t.to;
            return (
              <li key={t.to}>
                <Link
                  to={t.to}
                  aria-current={active ? 'page' : undefined}
                  className={clsx(
                    'relative inline-flex h-10 items-center whitespace-nowrap px-3 text-[14px]',
                    active ? 'font-semibold text-ink' : 'text-steel hover:text-ink',
                  )}
                >
                  {t.label}
                  {active && (
                    <span className="absolute inset-x-2 -bottom-px h-0.5 rounded bg-signal" aria-hidden />
                  )}
                </Link>
              </li>
            );
          })}
      </ul>
    </nav>
  );
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['meals'] });
}

// ---------- Заказ на день (ответственный) ----------

export interface MealsSearch {
  date?: string;
}

export function MealsPage() {
  const manage = useManage();
  const meta = useQuery(mealsMetaQuery);
  const search = useSearch({ strict: false }) as MealsSearch;
  const navigate = useNavigate();
  if (!manage) return <Navigate to="/meals/my" />;
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  const date = search.date ?? meta.data.nextDate ?? todayIso();
  const go = (d: string) => navigate({ to: '/meals', search: { date: d } });
  return (
    <div>
      <PageHeader title="Обеды" description="Кто обедает и сколько порций заказать" />
      <MealsNav />
      <DayStrip selected={date} onSelect={go} />
      <DayView date={date} onSelect={go} />
    </div>
  );
}

function DayStrip({ selected, onSelect }: { selected: string; onSelect: (d: string) => void }) {
  const from = addDaysIso(todayIso(), -1);
  const q = useQuery(mealDaysQuery(from, 15));
  const days = (q.data ?? []).filter((d) => d.working);
  return (
    <div className="mb-4 min-w-0 overflow-x-auto">
      <ul className="flex gap-1.5 pb-1">
        {days.map((d) => (
          <li key={d.date}>
            <button
              type="button"
              onClick={() => onSelect(d.date)}
              aria-pressed={d.date === selected}
              className={clsx(
                'flex h-14 w-[64px] flex-col items-center justify-center rounded-md border text-[12.5px] leading-tight',
                d.date === selected
                  ? 'border-graphite bg-graphite text-white'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <span className={d.date === selected ? 'text-white/75' : 'text-steel'}>
                {weekdayShort(d.date)} {Number(d.date.slice(8))}
              </span>
              <span className="text-[16px] font-semibold tabular-nums">{d.total}</span>
              {d.closed && <span className="sr-only">заказ зафиксирован</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DayView({ date, onSelect }: { date: string; onSelect: (d: string) => void }) {
  const q = useQuery(mealDayQuery(date));
  const invalidate = useInvalidate();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const setDay = useMutation({
    mutationFn: (v: { employeeId: string; eats: boolean | null }) =>
      api(`/meals/days/${date}/employees/${v.employeeId}`, { method: 'PUT', body: { eats: v.eats } }),
    onSuccess: () => setError(null),
    onError: (err) => setError(errorText(err)),
    onSettled: invalidate,
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const eating = d.lines.filter((l) => mealEats(l.status));
  const other = d.lines.filter((l) => !mealEats(l.status));
  const orderText = `Обеды на ${dayMonthText(date)} (${weekdayShort(date)}): ${d.total} ${portions(d.total)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(orderText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Не удалось скопировать — выделите текст вручную');
    }
  };

  return (
    <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <section className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Предыдущий день"
              onClick={() => onSelect(addDaysIso(date, -1))}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <h2 className="text-[18px] font-semibold">{dayTitle(date)}</h2>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Следующий день"
              onClick={() => onSelect(addDaysIso(date, 1))}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
          <Input
            type="date"
            aria-label="Дата"
            value={date}
            onChange={(e) => e.target.value && onSelect(e.target.value)}
            className="w-[160px]"
          />
        </div>
        {!d.working ? (
          <EmptyPanel title="Нерабочий день — обеды не заказываются">
            {d.calendarNote ?? 'Рабочие дни недели и праздники — во вкладке «Настройки».'}
          </EmptyPanel>
        ) : (
          <>
            <div className="panel mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
              <div>
                <p className="text-[34px] font-semibold leading-none tabular-nums">{d.total}</p>
                <p className="mt-1 text-[13px] text-steel">{portions(d.total)} всего</p>
              </div>
              <div className="text-[13.5px] text-ink-soft">
                <p>Сотрудников: {d.employees}</p>
                <p>Гостей: {d.guests}</p>
              </div>
              <div className="min-w-[200px] flex-1 text-[13.5px]">
                {d.closedAt ? (
                  <p className="flex items-start gap-1.5">
                    <Lock className="mt-0.5 size-3.5 shrink-0 text-steel" aria-hidden />
                    <span>
                      Заказ зафиксирован {atText(d.closedAt)}. Ваши правки пересчитают его, другие
                      ответственные получат уведомление.
                    </span>
                  </p>
                ) : (
                  <p>
                    Сотрудники могут отметиться до <b>{atText(d.cutoffAt)}</b> — в это время придёт итоговая
                    сводка.
                  </p>
                )}
              </div>
              <Button variant="secondary" size="sm" onClick={copy}>
                <Copy className="size-4" aria-hidden />
                {copied ? 'Скопировано' : 'Скопировать заказ'}
              </Button>
            </div>
            <FormError>{error}</FormError>
            <LinesPanel
              title={`Обедают — ${eating.length}`}
              lines={eating}
              empty="Никто не записан на этот день"
              pending={setDay.isPending}
              onSet={(employeeId, eats) => setDay.mutate({ employeeId, eats })}
            />
            {other.length > 0 && (
              <LinesPanel
                title={`Не обедают — ${other.length}`}
                lines={other}
                empty=""
                pending={setDay.isPending}
                onSet={(employeeId, eats) => setDay.mutate({ employeeId, eats })}
              />
            )}
          </>
        )}
      </section>
      {d.working && (
        <aside className="flex min-w-0 flex-col gap-4">
          <GuestsPanel date={date} items={d.guestItems} />
          <AddPersonPanel date={date} present={new Set(d.lines.map((l) => l.employee.id))} />
        </aside>
      )}
    </div>
  );
}

function LinesPanel({
  title,
  lines,
  empty,
  pending,
  onSet,
}: {
  title: string;
  lines: MealDayLine[];
  empty: string;
  pending: boolean;
  onSet: (employeeId: string, eats: boolean | null) => void;
}) {
  return (
    <section className="mb-4">
      <h3 className="mb-2 text-[15px] font-semibold">{title}</h3>
      {lines.length === 0 ? (
        <EmptyPanel title={empty} />
      ) : (
        <ul className="panel divide-y divide-line">
          {lines.map((l) => (
            <li key={l.employee.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate">{l.employee.fullName}</p>
                <p className="truncate text-[12.5px] text-steel">
                  {[l.employee.department, l.employee.position].filter(Boolean).join(' · ') || ' '}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={l.status} />
                {l.reason && <span className="text-[12.5px] text-steel">{l.reason}</span>}
              </div>
              <LineAction line={l} pending={pending} onSet={onSet} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LineAction({
  line,
  pending,
  onSet,
}: {
  line: MealDayLine;
  pending: boolean;
  onSet: (employeeId: string, eats: boolean | null) => void;
}) {
  const id = line.employee.id;
  if (line.status === 'absent' || line.status === 'off') return null;
  if (mealEats(line.status))
    return (
      <Button variant="ghost" size="sm" disabled={pending} onClick={() => onSet(id, false)}>
        Убрать
      </Button>
    );
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={pending}
      onClick={() => onSet(id, line.subscribed ? null : true)}
    >
      Вернуть
    </Button>
  );
}

function GuestsPanel({
  date,
  items,
}: {
  date: string;
  items: { id: string; qty: number; note: string | null; createdBy: string | null }[];
}) {
  const invalidate = useInvalidate();
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');
  const add = useMutation({
    mutationFn: () => api(`/meals/days/${date}/guests`, { method: 'POST', body: { qty: Number(qty), note } }),
    onSuccess: () => {
      setQty('1');
      setNote('');
    },
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/meals/guests/${id}`, { method: 'DELETE' }),
    onSettled: invalidate,
  });
  return (
    <section className="panel p-4">
      <h3 className="mb-1 text-[15px] font-semibold">Гости и доп. порции</h3>
      <p className="mb-3 text-[13px] text-steel">
        Посетители, практиканты, рабочие без учётки — порции сверх списка.
      </p>
      {items.length > 0 && (
        <ul className="mb-3 divide-y divide-line rounded-md border border-line">
          {items.map((g) => (
            <li key={g.id} className="flex items-center gap-2 px-3 py-2 text-[13.5px]">
              <span className="font-semibold tabular-nums">{g.qty}</span>
              <span className="min-w-0 flex-1 truncate">{g.note ?? 'без пояснения'}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Удалить: ${g.note ?? g.qty}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(g.id)}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <div className="grid grid-cols-[80px_1fr] gap-2">
          <Input
            type="number"
            min={1}
            max={200}
            aria-label="Порций"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <Input
            aria-label="Кто"
            placeholder="Кто, например «гости от поставщика»"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <FormError>{errorText(add.error)}</FormError>
        <Button type="submit" variant="secondary" size="sm" loading={add.isPending}>
          Добавить порции
        </Button>
      </form>
    </section>
  );
}

function AddPersonPanel({ date, present }: { date: string; present: Set<string> }) {
  const subs = useQuery(mealSubscriptionsQuery);
  const invalidate = useInvalidate();
  const [employeeId, setEmployeeId] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api(`/meals/days/${date}/employees/${employeeId}`, { method: 'PUT', body: { eats: true } }),
    onSuccess: () => setEmployeeId(''),
    onSettled: invalidate,
  });
  const options = (subs.data ?? []).filter((s) => !s.active && !present.has(s.employee.id));
  return (
    <section className="panel p-4">
      <h3 className="mb-1 text-[15px] font-semibold">Добавить на этот день</h3>
      <p className="mb-3 text-[13px] text-steel">Сотрудник не записан на обеды, но сегодня будет обедать.</p>
      <div className="flex flex-col gap-2">
        <Select aria-label="Сотрудник" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
          <option value="">Выберите сотрудника</option>
          {options.map((s) => (
            <option key={s.employee.id} value={s.employee.id}>
              {s.employee.fullName}
            </option>
          ))}
        </Select>
        <FormError>{errorText(add.error)}</FormError>
        <Button
          variant="secondary"
          size="sm"
          disabled={!employeeId}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          <UserPlus className="size-4" aria-hidden />
          Добавить
        </Button>
      </div>
    </section>
  );
}

// ---------- Мой обед ----------

export function MealsMyPage() {
  const meta = useQuery(mealsMetaQuery);
  const invalidate = useInvalidate();
  const from = todayIso();
  const days = useQuery({ ...mealsMyQuery(from, 14), enabled: Boolean(meta.data?.employee) });
  const [error, setError] = useState<string | null>(null);
  const sub = useMutation({
    mutationFn: (active: boolean) => api('/meals/my/subscription', { method: 'PUT', body: { active } }),
    onSuccess: () => setError(null),
    onError: (err) => setError(errorText(err)),
    onSettled: invalidate,
  });
  const choose = useMutation({
    mutationFn: (v: { date: string; eats: boolean | null }) =>
      api(`/meals/my/days/${v.date}`, { method: 'PUT', body: { eats: v.eats } }),
    onSuccess: () => setError(null),
    onError: (err) => setError(errorText(err)),
    onSettled: invalidate,
  });

  const header = (
    <>
      <PageHeader title="Обеды" description="Записаться на обеды или отказаться на конкретный день" />
      <MealsNav />
    </>
  );
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  const m = meta.data;
  if (!m.employee)
    return (
      <div>
        {header}
        <EmptyPanel title="К учётке не привязана карточка сотрудника">
          Обратитесь к администратору.
        </EmptyPanel>
      </div>
    );
  const exceptions = new Map(m.calendar.map((c) => [c.date, c]));
  const list = (days.data ?? []).filter((d) => d.working || exceptions.has(d.date));

  return (
    <div>
      {header}
      <div className="flex max-w-[760px] flex-col gap-5">
        <section className="panel flex flex-col items-start gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              {m.subscribed ? 'Вы записаны на обеды' : 'Вы не записаны на обеды'}
            </p>
            <p className="text-[13.5px] text-steel">
              {m.subscribed
                ? `Обед заказывается каждый рабочий день. Если не будете — отметьте день ниже до ${m.settings.deadlineTime} накануне. В отпуске и командировке обед снимается сам.`
                : 'Можно записаться насовсем или отметить отдельные дни ниже.'}
            </p>
          </div>
          <Button
            variant={m.subscribed ? 'secondary' : 'primary'}
            loading={sub.isPending}
            onClick={() => sub.mutate(!m.subscribed)}
          >
            {m.subscribed ? 'Отказаться от обедов' : 'Записаться на обеды'}
          </Button>
        </section>
        <FormError>{error}</FormError>
        <section>
          <h2 className="mb-2 text-[16px] font-semibold">Ближайшие дни</h2>
          {days.isPending ? (
            <Spinner />
          ) : days.error ? (
            <ErrorBox error={days.error} />
          ) : (
            <ul className="panel divide-y divide-line">
              {list.map((d) => (
                <MyDayRow
                  key={d.date}
                  day={d}
                  note={exceptions.get(d.date)?.note ?? null}
                  pending={choose.isPending}
                  onChoose={(eats) => choose.mutate({ date: d.date, eats })}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function MyDayRow({
  day: d,
  note,
  pending,
  onChoose,
}: {
  day: MealMyDay;
  note: string | null;
  pending: boolean;
  onChoose: (eats: boolean | null) => void;
}) {
  let action: { label: string; eats: boolean | null; primary?: boolean } | null = null;
  if (d.editable) {
    if (d.status === 'yes') action = { label: 'Не буду', eats: false };
    else if (d.status === 'extra') action = { label: 'Не буду', eats: null };
    else if (d.status === 'skip') action = { label: 'Буду', eats: null, primary: true };
    else if (d.status === 'no') action = { label: 'Буду', eats: true, primary: true };
  }
  return (
    <li className={clsx('flex items-center gap-3 px-4 py-2.5', !d.working && 'text-steel')}>
      <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-4">
        <div className="sm:w-[190px] sm:shrink-0">
          <p className="font-medium">
            {weekdayShort(d.date)}, {dayMonthText(d.date)}
          </p>
          {d.working && (
            <p className="text-[12px] text-steel">
              {d.editable
                ? `изменить до ${atText(d.cutoffAt)}`
                : d.closed || new Date(d.cutoffAt) < new Date()
                  ? 'заказ принят'
                  : ''}
            </p>
          )}
        </div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:mt-0">
          <span className="whitespace-nowrap">
            <StatusBadge status={d.status} />
          </span>
          {(d.reason || note) && <span className="text-[12.5px] text-steel">{d.reason ?? note}</span>}
        </div>
      </div>
      {action && (
        <Button
          variant={action.primary ? 'primary' : 'secondary'}
          size="sm"
          disabled={pending}
          onClick={() => onChoose(action.eats)}
        >
          {action.label}
        </Button>
      )}
    </li>
  );
}

// ---------- Кто обедает ----------

export function MealsPeoplePage() {
  const manage = useManage();
  const q = useQuery({ ...mealSubscriptionsQuery, enabled: manage });
  const invalidate = useInvalidate();
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [text, setText] = useState('');
  const toggle = useMutation({
    mutationFn: (v: { employeeId: string; active: boolean }) =>
      api(`/meals/subscriptions/${v.employeeId}`, { method: 'PUT', body: { active: v.active } }),
    onSettled: invalidate,
  });
  const items = useMemo(() => {
    const t = text.trim().toLowerCase();
    return (q.data ?? []).filter(
      (s) =>
        (filter === 'all' || s.active === (filter === 'active')) &&
        (!t || `${s.employee.fullName} ${s.employee.department ?? ''}`.toLowerCase().includes(t)),
    );
  }, [q.data, filter, text]);
  if (!manage) return <Navigate to="/meals/my" />;
  const active = (q.data ?? []).filter((s) => s.active).length;
  return (
    <div>
      <PageHeader
        title="Обеды"
        description="Кто записан на обеды постоянно. Рабочих без учётки записывает ответственный."
      />
      <MealsNav />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Segmented
          label="Фильтр"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `Все — ${q.data?.length ?? 0}` },
            { value: 'active', label: `Записаны — ${active}` },
            { value: 'inactive', label: 'Не записаны' },
          ]}
        />
        <div className="relative w-full sm:max-w-[260px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="ФИО или отдел"
            aria-label="Поиск сотрудника"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>
      <FormError>{errorText(toggle.error)}</FormError>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : items.length === 0 ? (
        <EmptyPanel title="Никого не нашли" />
      ) : (
        <ul className="panel divide-y divide-line">
          {items.map((s) => (
            <li key={s.employee.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  {s.employee.fullName}
                  {!s.employee.hasUser && <span className="ml-2 text-[12px] text-steel">без учётки</span>}
                </p>
                <p className="truncate text-[12.5px] text-steel">
                  {[s.employee.department, s.employee.position].filter(Boolean).join(' · ') || ' '}
                </p>
              </div>
              <Badge tone={s.active ? 'ok' : 'neutral'}>{s.active ? 'Активен' : 'Не записан'}</Badge>
              <Button
                variant={s.active ? 'ghost' : 'secondary'}
                size="sm"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ employeeId: s.employee.id, active: !s.active })}
              >
                {s.active ? 'Отозвать' : 'Записать'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Отчёт ----------

export interface MealsReportSearch {
  month?: string;
}

export function MealsReportPage() {
  const manage = useManage();
  const search = useSearch({ strict: false }) as MealsReportSearch;
  const navigate = useNavigate();
  const month = search.month ?? todayIso().slice(0, 7);
  const q = useQuery({ ...mealReportQuery(month), enabled: manage });
  if (!manage) return <Navigate to="/meals/my" />;
  return (
    <div>
      <PageHeader title="Обеды" description="Сколько обедов было за месяц — по зафиксированным заказам" />
      <MealsNav />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Месяц">
          {(p) => (
            <Input
              {...p}
              type="month"
              value={month}
              onChange={(e) =>
                e.target.value && navigate({ to: '/meals/report', search: { month: e.target.value } })
              }
              className="w-[180px]"
            />
          )}
        </Field>
        <a
          href={`/api/meals/report.csv?month=${month}`}
          className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-surface px-4 text-[14px] font-medium hover:bg-sunken"
        >
          <Download className="size-4" aria-hidden />
          Скачать для Excel
        </a>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : (
        <>
          <dl className="panel mb-4 grid grid-cols-3 divide-x divide-line">
            {[
              ['Порций', q.data.total],
              ['Из них гостей', q.data.guests],
              ['Дней с заказом', q.data.days.length],
            ].map(([k, v]) => (
              <div key={k} className="px-4 py-3">
                <dt className="text-[12.5px] text-steel">{k}</dt>
                <dd className="text-[22px] font-semibold tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
          {q.data.rows.length === 0 ? (
            <EmptyPanel title="За этот месяц заказов нет" />
          ) : (
            <div className="panel relative overflow-x-auto">
              <table className="w-full min-w-[480px] text-left">
                <thead>
                  <tr className="border-b border-line text-[13px] text-steel">
                    <th scope="col" className="px-4 py-2.5 font-medium">
                      Сотрудник
                    </th>
                    <th scope="col" className="px-4 py-2.5 font-medium">
                      Отдел
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">
                      Обедов
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {q.data.rows.map((r) => (
                    <tr key={r.employee.id}>
                      <td className="px-4 py-2.5">{r.employee.fullName}</td>
                      <td className="px-4 py-2.5 text-steel">{r.employee.department ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Настройки ----------

export function MealsSettingsPage() {
  const manage = useManage();
  const meta = useQuery(mealsMetaQuery);
  if (!manage) return <Navigate to="/meals/my" />;
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  return (
    <div>
      <PageHeader title="Обеды" description="Дедлайн заказа, рабочие дни и праздники" />
      <MealsNav />
      <div className="flex max-w-[760px] flex-col gap-5">
        <ScheduleForm key={meta.dataUpdatedAt} />
        <CalendarEditor />
      </div>
    </div>
  );
}

function ScheduleForm() {
  const meta = useQuery(mealsMetaQuery);
  const invalidate = useInvalidate();
  const [time, setTime] = useState(meta.data?.settings.deadlineTime ?? '20:00');
  const [workdays, setWorkdays] = useState<number[]>(meta.data?.settings.workdays ?? [1, 2, 3, 4, 5]);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => api('/meals/settings', { method: 'PUT', body: { deadlineTime: time, workdays } }),
    onSuccess: () => setSaved(true),
    onSettled: invalidate,
  });
  return (
    <section className="panel p-4">
      <h2 className="mb-3 text-[16px] font-semibold">Когда заказываем</h2>
      <div className="flex flex-col gap-4">
        <Field
          label="Дедлайн накануне"
          hint="До этого времени сотрудники отмечаются на следующий день; в это время приходит сводка."
        >
          {(p) => (
            <Input
              {...p}
              type="time"
              value={time}
              onChange={(e) => {
                setSaved(false);
                setTime(e.target.value);
              }}
              className="w-[140px]"
            />
          )}
        </Field>
        <fieldset className="min-w-0">
          <legend className="field-label">Рабочие дни недели</legend>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, i) => {
              const n = i + 1;
              const on = workdays.includes(n);
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    setSaved(false);
                    setWorkdays((w) => (on ? w.filter((x) => x !== n) : [...w, n].sort()));
                  }}
                  className={clsx(
                    'h-9 w-11 rounded-md border text-[13.5px]',
                    on
                      ? 'border-graphite bg-graphite text-white'
                      : 'border-line-strong bg-surface text-ink-soft',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <FormError>{errorText(save.error)}</FormError>
        <div className="flex items-center gap-3">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
          {saved && <span className="text-[13px] text-ok">Сохранено</span>}
        </div>
      </div>
    </section>
  );
}

function CalendarEditor() {
  const meta = useQuery(mealsMetaQuery);
  const invalidate = useInvalidate();
  const [date, setDate] = useState('');
  const [isWorking, setIsWorking] = useState('false');
  const [note, setNote] = useState('');
  const set = useMutation({
    mutationFn: (body: { date: string; isWorking: boolean | null; note?: string }) =>
      api('/meals/settings/calendar', { method: 'PUT', body }),
    onSuccess: () => {
      setDate('');
      setNote('');
    },
    onSettled: invalidate,
  });
  const list = (meta.data?.calendar ?? []).filter((c) => c.date >= todayIso());
  return (
    <section className="panel p-4">
      <h2 className="mb-1 text-[16px] font-semibold">Праздники и рабочие выходные</h2>
      <p className="mb-3 text-[13px] text-steel">
        Праздник в будни — обед не заказывается. Рабочая суббота — заказывается как в обычный день.
      </p>
      {list.length > 0 && (
        <ul className="mb-4 divide-y divide-line rounded-md border border-line">
          {list.map((c) => (
            <li key={c.date} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13.5px]">
              <span className="w-[150px] font-medium">
                {weekdayShort(c.date)}, {dayMonthText(c.date)}
              </span>
              <Badge tone={c.isWorking ? 'ok' : 'neutral'}>{c.isWorking ? 'Рабочий' : 'Нерабочий'}</Badge>
              <span className="min-w-0 flex-1 truncate text-steel">{c.note}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Убрать ${dayMonthText(c.date)}`}
                onClick={() => set.mutate({ date: c.date, isWorking: null })}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="grid gap-3 sm:grid-cols-[160px_150px_1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (date) set.mutate({ date, isWorking: isWorking === 'true', note });
        }}
      >
        <Field label="Дата">
          {(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
        <Field label="День">
          {(p) => (
            <Select {...p} value={isWorking} onChange={(e) => setIsWorking(e.target.value)}>
              <option value="false">Нерабочий</option>
              <option value="true">Рабочий</option>
            </Select>
          )}
        </Field>
        <Field label="Пояснение">
          {(p) => (
            <Input
              {...p}
              value={note}
              placeholder="Например, «День народного единства»"
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <Button type="submit" variant="secondary" disabled={!date} loading={set.isPending}>
          Добавить
        </Button>
      </form>
      <div className="mt-2">
        <FormError>{errorText(set.error)}</FormError>
      </div>
    </section>
  );
}
